import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Check, Loader2, ArrowRight, XCircle } from "lucide-react";

const MAX_POLLS = 10;
const INTERVAL = 2000;

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const { authAxios } = useAuth();
    const { clear } = useCart();
    const [status, setStatus] = useState("polling"); // polling | paid | expired | error | timeout
    const [txn, setTxn] = useState(null);
    const tries = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            tries.current += 1;
            try {
                const { data } = await authAxios.get(`/checkout/status/${sessionId}`);
                if (cancelled) return;
                setTxn(data.transaction);
                if (data.status === "paid") {
                    setStatus("paid");
                    clear();
                    return;
                }
                if (data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                if (tries.current >= MAX_POLLS) {
                    setStatus("timeout");
                    return;
                }
                setTimeout(poll, INTERVAL);
            } catch {
                setStatus("error");
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId, authAxios, clear]);

    return (
        <main className="min-h-screen bg-limestone-100 pt-28 pb-24 flex items-start justify-center" data-testid="checkout-success-page">
            <div className="mx-auto w-full max-w-2xl px-5 pt-12">
                {status === "polling" && (
                    <div className="rounded-card border-2 border-ink-200 bg-white p-12 text-center" data-testid="checkout-polling">
                        <Loader2 className="mx-auto h-12 w-12 animate-spin text-madder-500" />
                        <h1 className="mt-6 font-display text-[28px] font-bold tracking-tight text-ink-800">Confirming your payment…</h1>
                        <p className="mt-3 text-[16px] font-medium text-ink-600">This usually takes a few seconds.</p>
                    </div>
                )}

                {status === "paid" && (
                    <div className="rounded-card border-2 border-redsea-500 bg-white p-12 text-center shadow-pop" data-testid="checkout-paid">
                        <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-redsea-500 text-white">
                            <Check className="h-8 w-8" strokeWidth={3.5} />
                        </div>
                        <p className="eyebrow mt-6">Payment confirmed</p>
                        <h1 className="mt-3 font-display text-[36px] font-bold tracking-tight text-ink-800">Your domains are live.</h1>
                        <p className="mt-4 text-[17px] font-medium text-ink-600">
                            Registration complete. Manage DNS and mailboxes from your dashboard.
                        </p>
                        {txn && (
                            <div className="mt-8 rounded-[8px] border-2 border-ink-200 bg-mudbrick-100 p-5 text-start">
                                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-600 mb-2">Order summary</p>
                                {(txn.items || []).map((it) => (
                                    <div key={it.domain} className="flex items-center justify-between py-1.5">
                                        <span className="font-mono text-[14px] font-bold text-ink-800 num-ltr">{it.domain}</span>
                                        <span className="font-mono text-[14px] tabular-nums num-ltr text-ink-700">{it.line_total_sar} SAR</span>
                                    </div>
                                ))}
                                <div className="mt-3 border-t-2 border-ink-200 pt-3 flex items-center justify-between">
                                    <span className="text-[13px] font-bold text-ink-800">Total</span>
                                    <span className="font-mono text-[18px] font-extrabold tabular-nums num-ltr text-madder-500">{txn.amount_sar} SAR</span>
                                </div>
                            </div>
                        )}
                        <Link to="/dashboard" className="mt-8 inline-flex h-[60px] items-center justify-center gap-2 rounded-btn bg-madder-500 px-8 text-[17px] font-bold text-white hover:bg-madder-600" data-testid="checkout-goto-dashboard">
                            Go to dashboard
                            <ArrowRight className="h-5 w-5 rtl-mirror" />
                        </Link>
                    </div>
                )}

                {(status === "expired" || status === "error" || status === "timeout") && (
                    <div className="rounded-card border-2 border-madder-500 bg-white p-12 text-center" data-testid="checkout-error">
                        <XCircle className="mx-auto h-12 w-12 text-madder-500" />
                        <h1 className="mt-6 font-display text-[28px] font-bold tracking-tight text-ink-800">
                            {status === "expired" ? "Session expired" : status === "timeout" ? "Taking longer than usual" : "Something went wrong"}
                        </h1>
                        <p className="mt-3 text-[16px] font-medium text-ink-600">
                            {status === "expired" ? "Your checkout session timed out. Please try again." : "Please return to your cart and retry."}
                        </p>
                        <Link to="/cart" className="mt-6 inline-flex h-[56px] items-center gap-2 rounded-btn border-2 border-ink-800 bg-white px-6 text-[16px] font-bold text-ink-800 hover:bg-ink-800 hover:text-white">
                            Back to cart
                        </Link>
                    </div>
                )}
            </div>
        </main>
    );
}
